import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Save, Check, Ban, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { decimalToHMS } from "@/lib/time";
import { cn } from "@/lib/cn";
import { Table, THead, TBody, TR, TH } from "@/components/ui-kit/Table";
import { Button } from "@/components/ui-kit/Button";
import { EntryRow } from "./EntryRow";
import { PresetLine } from "./PresetLine";

const TABS = ["All", "Needs Review", "Approved", "Excluded", "Invoiced"];
const TAB_STATUS = { "Needs Review": "Draft", Approved: "Approved", Excluded: "Excluded", Invoiced: "Invoiced" };

const todayStr = () => new Date().toISOString().slice(0, 10);
const seed = (e) => ({
  date: (e.date || "").slice(0, 10) || todayStr(),
  hours: String(e.decimal_hours ?? 0),
  description: e.description || "",
  template: e.template || "",
  labels: Array.isArray(e.labels) ? e.labels : [],
});

export function EntryGrid({ matterId, matter, presets, templates, search, onChanged, extraActions }) {
  const [entries, setEntries] = useState(null);
  const [drafts, setDrafts] = useState([]); // [{ tempId }]
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [activeKey, setActiveKey] = useState(null);
  const [focusKey, setFocusKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("All");

  const rate = matter.hourly_rate;
  const increment = matter.billing_increment_min;
  const requireReview = matter.require_time_review;

  const load = useCallback(() => {
    api.get(`/matters/${matterId}/time-entries`).then((r) => {
      setEntries(r.data);
      // Seed an edit for each new server entry (never clobber unsaved edits).
      setEdits((prev) => {
        const next = { ...prev };
        for (const e of r.data) if (!(e.id in next)) next[e.id] = seed(e);
        return next;
      });
    }).catch(() => {});
  }, [matterId]);
  useEffect(() => { load(); }, [load]);

  const isDirty = useCallback(
    (e) => {
      const ed = edits[e.id];
      if (!ed) return false;
      return (
        ed.date !== (e.date || "").slice(0, 10) ||
        Number(ed.hours) !== Number(e.decimal_hours) ||
        ed.description !== (e.description || "") ||
        (ed.template || "") !== (e.template || "") ||
        JSON.stringify(ed.labels || []) !== JSON.stringify(e.labels || [])
      );
    },
    [edits]
  );

  const dirtyCount = useMemo(
    () => drafts.length + (entries || []).filter(isDirty).length,
    [drafts, entries, isDirty]
  );

  // Unsaved-changes guard.
  useEffect(() => {
    const handler = (ev) => {
      if (dirtyCount > 0) {
        ev.preventDefault();
        ev.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirtyCount]);

  const counts = useMemo(() => {
    const c = { All: (entries || []).length + drafts.length, "Needs Review": 0, Approved: 0, Excluded: 0, Invoiced: 0 };
    for (const e of entries || []) if (c[e.status] !== undefined) c[e.status === "Draft" ? "Needs Review" : e.status]++;
    return c;
  }, [entries, drafts]);

  const setField = (key, field, value) =>
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const addRow = () => {
    const tempId = `new-${Date.now()}`;
    setDrafts((d) => [{ tempId }, ...d]);
    setEdits((prev) => ({ ...prev, [tempId]: { date: todayStr(), hours: "", description: "", template: "", labels: [] } }));
    setActiveKey(tempId);
    setFocusKey(tempId);
  };

  const presetAdd = (h) => {
    const key = activeKey || drafts[0]?.tempId;
    if (!key) return;
    const cur = Number(edits[key]?.hours || 0);
    setField(key, "hours", String(Math.round((cur + h) * 10) / 10));
  };

  const buildBody = (ed, withSource) => {
    const { hours, minutes, seconds } = decimalToHMS(Number(ed.hours));
    const labels = Array.isArray(ed.labels) ? ed.labels : [];
    const body = {
      date: new Date(ed.date).toISOString(),
      hours, minutes, seconds,
      description: (ed.description || "").trim(),
      // Keep the single `template` in sync for back-compat (first label wins).
      template: labels[0] || ed.template || "",
      labels,
    };
    if (withSource) body.source = "Workbench";
    return body;
  };

  const saveRow = async (key) => {
    const ed = edits[key];
    if (!ed || !(Number(ed.hours) > 0)) return;
    setSavingKey(key);
    try {
      if (String(key).startsWith("new-")) {
        await api.post(`/matters/${matterId}/time-entries`, buildBody(ed, true));
        setDrafts((d) => d.filter((x) => x.tempId !== key));
        setEdits((prev) => {
          const n = { ...prev };
          delete n[key];
          return n;
        });
      } else {
        await api.patch(`/time-entries/${key}`, buildBody(ed, false));
      }
      load();
      onChanged?.();
    } finally {
      setSavingKey(null);
    }
  };

  const saveAll = async () => {
    setBusy(true);
    try {
      for (const d of drafts) {
        const ed = edits[d.tempId];
        if (ed && Number(ed.hours) > 0) await api.post(`/matters/${matterId}/time-entries`, buildBody(ed, true));
      }
      for (const e of entries || []) {
        if (isDirty(e)) await api.patch(`/time-entries/${e.id}`, buildBody(edits[e.id], false));
      }
      setDrafts([]);
      load();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (key, status) => {
    await api.patch(`/time-entries/${key}`, { status });
    load();
    onChanged?.();
  };

  const deleteRow = async (key) => {
    if (String(key).startsWith("new-")) {
      setDrafts((d) => d.filter((x) => x.tempId !== key));
      setEdits((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
      return;
    }
    await api.delete(`/time-entries/${key}`);
    load();
    onChanged?.();
  };

  const bulk = async (status) => {
    const ids = [...selected].filter((id) => !String(id).startsWith("new-"));
    if (!ids.length) return;
    setBusy(true);
    try {
      await api.post(`/matters/${matterId}/time-entries/bulk-status`, { entry_ids: ids, status });
      setSelected(new Set());
      load();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const q = (search || "").trim().toLowerCase();
  const visible = (entries || []).filter((e) => {
    if (tab !== "All" && e.status !== TAB_STATUS[tab]) return false;
    if (!q) return true;
    return (
      (e.description || "").toLowerCase().includes(q) ||
      (e.template || "").toLowerCase().includes(q) ||
      (e.source || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="entry-grid" className="space-y-3">
      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={addRow} data-testid="add-time-entry">
          <Plus className="h-4 w-4" /> Add time entry
        </Button>
        <Button size="sm" variant="secondary" onClick={saveAll} disabled={busy || dirtyCount === 0} data-testid="save-changes">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save changes{dirtyCount > 0 ? ` (${dirtyCount})` : ""}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => bulk("Approved")} disabled={busy || !selected.size} data-testid="approve-selected">
          <Check className="h-4 w-4" /> Approve selected
        </Button>
        <Button size="sm" variant="secondary" onClick={() => bulk("Excluded")} disabled={busy || !selected.size} data-testid="exclude-selected">
          <Ban className="h-4 w-4" /> Exclude selected
        </Button>
        {extraActions}
      </div>

      {/* Quick-add preset line (left) + filter tabs (right) — swapped so the
          quick-add buttons sit near the grid's Hours column / Add button. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PresetLine presets={presets} onAdd={presetAdd} disabled={!activeKey && !drafts.length} />
        <div className="flex flex-wrap gap-1.5" data-testid="grid-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`grid-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setTab(t)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
                tab === t
                  ? "border-primary/40 bg-primary/10 text-primary"
                  : "border-border bg-surface text-muted-foreground hover:bg-accent"
              )}
            >
              {t} <span className="ml-1 text-xs opacity-70">{counts[t] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {entries === null ? (
        <div className="grid place-items-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : drafts.length === 0 && visible.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="grid-empty">
          {tab === "All" ? "No time logged yet — click “Add time entry”." : `No ${tab.toLowerCase()} entries.`}
        </div>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH className="w-8" />
              <TH>Date</TH>
              <TH>Hours</TH>
              <TH>Legal service performed</TH>
              <TH>Tasks</TH>
              <TH className="text-right">Amount</TH>
              <TH>Status</TH>
              <TH>Source</TH>
              <TH className="text-right">Actions</TH>
            </TR>
          </THead>
          <TBody>
            {drafts.map((d) => (
              <EntryRow
                key={d.tempId}
                rowKey={d.tempId}
                data={edits[d.tempId] || { date: todayStr(), hours: "", description: "", template: "" }}
                isNew
                isDirty
                templates={templates}
                rate={rate}
                increment={increment}
                requireReview={requireReview}
                autoFocus={focusKey === d.tempId}
                saving={savingKey === d.tempId}
                onField={setField}
                onFocusHours={setActiveKey}
                onSelect={() => {}}
                onSave={saveRow}
                onDelete={deleteRow}
                onStatus={setStatus}
              />
            ))}
            {visible.map((e) => (
              <EntryRow
                key={e.id}
                rowKey={e.id}
                entry={e}
                data={edits[e.id] || seed(e)}
                isNew={false}
                isDirty={isDirty(e)}
                templates={templates}
                rate={rate}
                increment={increment}
                requireReview={requireReview}
                selected={selected.has(e.id)}
                saving={savingKey === e.id}
                onField={setField}
                onFocusHours={setActiveKey}
                onSelect={toggleSelect}
                onSave={saveRow}
                onDelete={deleteRow}
                onStatus={setStatus}
              />
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
